import React, {FC, useEffect, useRef, useState} from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as CheckedDisabledIcon} from '../../../../src/assets/images/icon-checked-disabled.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/active-unchecked.svg';
import {ReactComponent as InactiveIcon} from '../../../../src/assets/images/icon-unchecked-inactive.svg';
import {ReactComponent as PreselectedIcon} from '../../../../src/assets/images/checkbox-preselected.svg';
import './ArrTransferList.scss';
import ArrTooltip from '../../../components/common/arr-tooltip/ArrTooltip';
import ArrLoadingSpinner from '../arr-loading-spinner/ArrLoadingSpinner';
import ArrRadio, {RadioButtonProps} from '../arr-radio/ArrRadio';
import {t} from 'i18next';
import ArrButton from '../arr-button/ArrButton';
import ArrTextField, {ArrTextFieldProps} from '../arr-text-field/ArrTextField';

export type KeyValue = {
  id: number | string;
  label: string;
  isDefault?: boolean;
  disabled?: boolean;
  dataType?: string;
  name?: string;
  subLabel?: string;
};

interface IArrTransferList {
  displayedList: KeyValue[];
  selectedList: KeyValue[];
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => void;
  title?: string;
  showCheckbox?: boolean;
  listItemContainerClass?: string;
  disable?: boolean;
  isLoader?: boolean;
  endIcon?: React.ReactNode | ((item: KeyValue) => React.ReactNode);
  endIconPosition?: 'inline' | 'right';
  preSelected?: boolean;
  renderHeaderButton?: React.ReactNode;
  showRadio?: boolean;
  onRadioBtnClick?: (field: KeyValue) => void;
  radioSize?: RadioButtonProps['size'];
  showHeaderAction?: boolean;
  headerActionLabel?: string;
  onHeaderAction?: () => void;
  headerActionDisable?: boolean;
  clickOnRow?: boolean;
  hideItemDivider?: boolean;
  showSearchField?: boolean;
  searchProps?: ArrTextFieldProps;
  fullWidthSearchField?: boolean;
  checkboxDataTestId?: string;
  dimSelect?: boolean;
  showHeaderBorder?: boolean;
  disableItemTabFocus?: boolean;
}

const ArrTransferList: FC<IArrTransferList> = ({
  displayedList,
  selectedList,
  onChange,
  title = '',
  showCheckbox = true,
  listItemContainerClass = '',
  disable = false,
  isLoader,
  endIcon,
  endIconPosition = 'inline',
  preSelected = false,
  renderHeaderButton = null,
  showRadio,
  radioSize = 'small',
  onRadioBtnClick,
  showHeaderAction = false,
  headerActionLabel,
  onHeaderAction,
  headerActionDisable = false,
  clickOnRow = false,
  hideItemDivider = false,
  showSearchField = false,
  searchProps,
  fullWidthSearchField,
  checkboxDataTestId = 'arr-transfer-list',
  dimSelect = false,
  showHeaderBorder = true,
  disableItemTabFocus = false,
}) => {
  const safeSelectedList: KeyValue[] = Array.isArray(selectedList)
    ? selectedList
    : [];
  const isFieldSelected = (field: KeyValue) =>
    safeSelectedList.some((item) => item.id === field.id);
  const textRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [truncatedItems, setTruncatedItems] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (displayedList.length !== 0) {
      const updated: Record<string, boolean> = {};

      displayedList.forEach((item) => {
        const key = String(item.id);
        // eslint-disable-next-line security/detect-object-injection
        const el = textRefs.current[key];
        if (el) {
          // eslint-disable-next-line security/detect-object-injection
          updated[key] = el.scrollWidth > el.clientWidth;
        }
      });

      setTruncatedItems(updated);
    }
  }, [displayedList]);

  const enableRowKeyboardSelection = disableItemTabFocus && !showCheckbox && !showRadio;

  return (
    <Box className={`container-list ${listItemContainerClass}`}>
      <div
        className={showHeaderBorder ? 'title-container-box' : ''}
        data-testid={`arr-transfer-list-div-${checkboxDataTestId}`}
      >
        <Box className="title-container">
          {title && (
            <Box className="title-content">
              <Typography
                className="typo-text subhead4_size text box-sub-heading"
                data-testid={`arr-transfer-list-title-${displayedList[0]?.label}`}
                tabIndex={0}
              >
                {title}
              </Typography>
              {showHeaderAction && (
                <ArrButton
                  size="small"
                  onClick={onHeaderAction}
                  disabled={disable || headerActionDisable}
                  dataTestId="arr-transfer-list-button"
                  tabIndex={0}
                >
                  {headerActionLabel}
                </ArrButton>
              )}
            </Box>
          )}
          {renderHeaderButton && <>{renderHeaderButton}</>}
        </Box>
        {showSearchField && (
          <Box
            className={`transfer-search-box ${fullWidthSearchField ? 'full-width-searchbox' : ''}`}
          >
            <ArrTextField
              sizeVariant="medium"
              placeholder="Search"
              className="search-textfield"
              {...searchProps}
              dataTestId={`tl-search-text-${displayedList[0]?.label}`}
              tabIndex={disableItemTabFocus ? undefined : 0}
            />
          </Box>
        )}
      </div>
      <List className="list-item">
        {isLoader ? (
          <ArrLoadingSpinner />
        ) : (
          displayedList.map((field) => {
            const checked = isFieldSelected(field);

            return (
              <ListItem
                key={field.id}
                onClick={(e) => {
                  if (!field.disabled && !disable && clickOnRow) {
                    onChange(e as any, field);
                  }
                }}
                onKeyDown={
                  enableRowKeyboardSelection
                    ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onChange(e as any, field);
                      }
                    }
                    : undefined
                }
                className={`list-item-row
                  ${checked ? (dimSelect ? 'click-on-row-bg' : 'list-item-active') : ''}
                  ${preSelected && checked && 'list-item-preselected'}
                  ${field.disabled ? 'row-disabled' : ''}
                  ${hideItemDivider ? 'list-item-row-no-divider' : ''}
                `}
                data-testid={`arr-transfer-list-list-item-${field.id}-${field.label}`}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center">
                      {showCheckbox && (
                        <Checkbox
                          data-testid={`${checkboxDataTestId}-checkbox-${field.id}`}
                          checked={checked}
                          onChange={(e) => onChange(e, field)}
                          role="checkbox"
                          slotProps={{
                            input: {
                              tabIndex: 0,
                              'aria-labelledby': checkboxDataTestId + `-${field.id}`,
                            },
                          }}
                          onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              const input = (
                                event.currentTarget as HTMLElement
                              ).querySelector(
                                'input[type="checkbox"]'
                              ) as HTMLInputElement | null;
                              input?.click();
                            }
                          }}
                          className={
                            checked && !field.disabled
                              ? 'list-item-checkbox-active'
                              : 'list-item-checkbox'
                          }
                          disabled={disable || field.disabled}
                          icon={
                            disable || field.disabled ? (
                              <InactiveIcon />
                            ) : (
                              <UnCheckedIcon />
                            )
                          }
                          checkedIcon={
                            preSelected ? (
                              <PreselectedIcon />
                            ) : disable || field.disabled ? (
                              <CheckedDisabledIcon />
                            ) : (
                              <CheckedIcon />
                            )
                          }
                          id={field.label}
                          name={field.label}
                        />
                      )}
                      <div
                        className={`transfer-label-container ${(endIcon || showRadio) && 'label-icon'}`}
                        data-testid={`arr-transfer-list-div-btn-${field.label}`}
                      >
                        <ArrTooltip
                          content={field.label}
                          isTruncated={truncatedItems[String(field.id)]}
                          showOnTruncate={true}
                          tabIndex={disableItemTabFocus ? undefined : 0}
                        >
                          <Typography
                            ref={(el) => {
                              textRefs.current[String(field.id)] = el;
                            }}
                            className={`typo-text body3_size ${preSelected && (disable || field.disabled) ? 'label-preselected' : disable || field.disabled ? 'label-inactive' : 'box-sub-heading'}`}
                            noWrap
                            id="transfer-list-item-label"
                            data-testid={`transfer-list-item-label-${field.id}-${field.label}`}
                            tabIndex={disableItemTabFocus ? 0 : undefined }
                          >
                            {field.label}
                            {field.subLabel && (
                              <Typography
                                component="span"
                                className="typo-text body3_size arr-transerlist-sublabel"
                                data-testid={`arr-transfer-list-sublabel-${displayedList[0]?.label}`}
                                tabIndex={disableItemTabFocus ? undefined : 0}
                              >
                                {' '}
                                {field.subLabel}
                              </Typography>
                            )}
                          </Typography>
                        </ArrTooltip>
                        {endIconPosition === 'inline' && endIcon && (
                          <Box className="end-icon-container-inline">
                            {typeof endIcon === 'function'
                              ? endIcon(field)
                              : endIcon}
                          </Box>
                        )}
                        {showRadio && (
                          <ArrRadio
                            size={radioSize}
                            dataTestId="controlled-radio-buttons-group"
                            name="controlled-radio-buttons-group"
                            className={`radio-container`}
                            classNameFormControlLabel={
                              field.isDefault
                                ? 'custom-form-control-label-default'
                                : 'custom-form-control-label'
                            }
                            onChange={() => onRadioBtnClick?.(field)}
                            value={
                              field.isDefault
                                ? t('common.arrTransferList.default')
                                : t('common.arrTransferList.setAsDefault')
                            }
                            data={[
                              {
                                label: field.isDefault
                                  ? t('common.arrTransferList.default')
                                  : t('common.arrTransferList.setAsDefault'),
                                value: t('common.arrTransferList.default'),
                              },
                            ]}
                          />
                        )}
                      </div>
                    </Box>
                  }
                />
                {endIconPosition === 'right' && endIcon && (
                  <Box className="end-icon-container-right">
                    {typeof endIcon === 'function' ? endIcon(field) : endIcon}
                  </Box>
                )}
              </ListItem>
            );
          })
        )}
      </List>
    </Box>
  );
};

export default ArrTransferList;
